import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { useLanguage } from '@/contexts/LanguageContext';
import { getBlogPosts } from '@/data/blogPosts';

const Blog = () => {
  const { language } = useLanguage();
  const posts = getBlogPosts(language);
  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Blog Quimical: cal virgem, indústria e inovação"
        description="Artigos sobre cal virgem, cal hidratada, processos industriais e sustentabilidade no setor de cales."
        canonical="https://quimical.com.br/blog"
      />
      <Header />
      <main className="pt-16">
        {/* Hero Section - Mobile First */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background px-4 py-12 md:py-20">
          <div className="container mx-auto max-w-4xl text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary">
              <BookOpen className="mr-1.5 h-3 w-3" />
              Blog Quimical
            </Badge>
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
              Conteúdos para profissionais da indústria
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base md:text-lg">
              Tendências, análises técnicas e guias práticos para apoiar suas operações.
            </p>
          </div>
        </section>

        {/* Featured Post - Mobile First */}
        <section className="px-4 py-8 md:py-12">
          <div className="container mx-auto">
            <Link
              to={`/post/${featuredPost.slug}`}
              className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-colors hover:border-primary/30 md:rounded-3xl"
            >
              <div className="relative aspect-[16/10] overflow-hidden sm:aspect-[16/8] md:aspect-[16/7]">
                <img
                  src={featuredPost.image_url}
                  alt={featuredPost.title}
                  className="h-full w-full object-cover "
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                  <Badge className="mb-2 bg-primary/90 text-primary-foreground md:mb-3">
                    Destaque
                  </Badge>
                  <h2 className="text-lg font-bold leading-tight text-foreground sm:text-xl md:text-2xl lg:text-3xl">
                    {featuredPost.title}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground sm:text-base md:line-clamp-3">
                    {featuredPost.excerpt}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground sm:text-sm md:mt-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(featuredPost.published_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="flex items-center gap-1.5 text-primary">
                      Ler artigo
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Posts Grid - Mobile First */}
        <section className="px-4 pb-16 md:pb-20">
          <div className="container mx-auto">
            <div className="mb-6 flex items-center justify-between md:mb-8">
              <h3 className="text-lg font-semibold text-foreground sm:text-xl md:text-2xl">
                Todos os artigos
              </h3>
              <span className="text-xs text-muted-foreground sm:text-sm">
                {posts.length} publicações
              </span>
            </div>

            {/* Mobile: Stack / Tablet+: Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
              {otherPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/post/${post.slug}`}
                  className="group flex flex-row gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary/30 sm:flex-col sm:gap-0 sm:p-0"
                >
                  {/* Mobile: Side image / Tablet+: Top image */}
                  <div className="relative h-20 w-24 flex-shrink-0 overflow-hidden rounded-lg sm:aspect-[16/10] sm:h-auto sm:w-full sm:rounded-b-none sm:rounded-t-xl">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="h-full w-full object-cover "
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="flex flex-1 flex-col justify-center sm:p-4">
                    <Badge variant="secondary" className="mb-1.5 w-fit text-[10px] capitalize sm:mb-2 sm:text-xs">
                      {post.category}
                    </Badge>
                    <h4 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground group-hover:text-primary sm:text-base">
                      {post.title}
                    </h4>
                    <p className="mt-1 line-clamp-2 hidden text-sm text-muted-foreground sm:block">
                      {post.excerpt}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground sm:mt-3 sm:text-xs">
                      <Calendar className="h-3 w-3" />
                      {new Date(post.published_at).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="bg-muted/30 px-4 py-12 md:py-16">
          <div className="container mx-auto max-w-xl text-center">
            <h2 className="text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
              Receba nossas novidades
            </h2>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              Inscreva-se para receber os novos artigos técnicos e atualizações do setor.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                placeholder="Seu e-mail"
                className="flex-1 rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button className="rounded-xl px-6">
                Inscrever-se
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;

